import React, { forwardRef, type InputHTMLAttributes, useRef } from 'react';

import { cls } from '@/utils/cls';

import { inputStyle } from './input';

export type FileInputProps = {
  onChange?: (file: File | null) => void;
  onIsMouseDragChange?: (isMouseDrag: boolean) => void;
  error?: boolean | string;
  hint?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'>;

/**
 * @todo multiple file support
 */
export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  function FileUpload(
    {
      className,
      onChange,
      onIsMouseDragChange,
      children,
      disabled,
      error,
      hint,
      ...props
    }: FileInputProps,
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    const style = fileInputStyle({
      disabled,
      error: Boolean(error),
    });
    const helperText = typeof error === 'string' ? error : hint;

    function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
      if (event.target.files) {
        onChange?.(event.target.files[0]);
      }
    }

    function handleDragOver(event: React.DragEvent<HTMLDivElement>) {
      event.preventDefault();
      event.stopPropagation();
      onIsMouseDragChange?.(true);
    }

    function handleDragLeave(event: React.DragEvent<HTMLDivElement>) {
      event.preventDefault();
      event.stopPropagation();
      onIsMouseDragChange?.(false);
    }

    function handleDrop(event: React.DragEvent<HTMLDivElement>) {
      event.preventDefault();
      event.stopPropagation();
      onIsMouseDragChange?.(false);
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        const file = event.dataTransfer.files[0];
        onChange?.(file);
        if (inputRef.current) {
          inputRef.current.files = event.dataTransfer.files;
        }
        event.dataTransfer.clearData();
      }
    }

    return (
      <div>
        <div
          className={cls(
            style.container(),
            style.input({ className }),
            'cursor-pointer',
          )}
          ref={ref}
          onClick={() => inputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          tabIndex={0}
        >
          <input
            {...props}
            ref={inputRef}
            multiple={false}
            type="file"
            onChange={handleFileChange}
            className="hidden"
          />
          {children}
        </div>
        {helperText ? (
          <div className={style.helperText()}>{helperText}</div>
        ) : null}
      </div>
    );
  },
);

export const fileInputStyle = inputStyle;
